import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Package, 
  Calendar,
  Download,
  BarChart3,
  PieChart,
  Loader2,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [dateRange, setDateRange] = useState('all');
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    fetchReportData();
  }, [dateRange]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      // Get dashboard stats
      const statsRes = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(statsRes.data);

      // Get orders for detailed analysis
      const ordersRes = await axios.get(`${API}/orders?include_preorders=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Process orders for reports
      const orders = ordersRes.data || [];
      const now = new Date();
      
      // Filter by date range
      let filteredOrders = orders;
      if (dateRange === '7days') {
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        filteredOrders = orders.filter(o => new Date(o.created_at) >= sevenDaysAgo);
      } else if (dateRange === '30days') {
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        filteredOrders = orders.filter(o => new Date(o.created_at) >= thirtyDaysAgo);
      } else if (dateRange === '90days') {
        const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        filteredOrders = orders.filter(o => new Date(o.created_at) >= ninetyDaysAgo);
      }

      // Calculate metrics
      const paidOrders = filteredOrders.filter(o => o.payment_status === 'paid');
      const pendingOrders = filteredOrders.filter(o => ['pending_cash', 'pending_etransfer', 'unpaid'].includes(o.payment_status));
      
      const totalRevenue = paidOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      const pendingRevenue = pendingOrders.reduce((sum, o) => sum + (o.total || 0), 0);
      
      // Revenue by payment method - both paid AND pending
      const revenueByMethod = {
        card: {
          paid: paidOrders.filter(o => o.payment_method === 'stripe' || o.payment_method === 'card').reduce((sum, o) => sum + (o.total || 0), 0),
          pending: pendingOrders.filter(o => o.payment_method === 'stripe' || o.payment_method === 'card').reduce((sum, o) => sum + (o.total || 0), 0),
        },
        cash: {
          paid: paidOrders.filter(o => o.payment_method === 'cash').reduce((sum, o) => sum + (o.total || 0), 0),
          pending: pendingOrders.filter(o => o.payment_method === 'cash').reduce((sum, o) => sum + (o.total || 0), 0),
        },
        etransfer: {
          paid: paidOrders.filter(o => o.payment_method === 'etransfer').reduce((sum, o) => sum + (o.total || 0), 0),
          pending: pendingOrders.filter(o => o.payment_method === 'etransfer').reduce((sum, o) => sum + (o.total || 0), 0),
        },
      };

      // Orders by type
      const preorderCount = filteredOrders.filter(o => o.order_type === 'pre-order').length;
      const regularCount = filteredOrders.filter(o => o.order_type !== 'pre-order').length;

      // Revenue by order type - both paid AND pending
      const preorderRevenue = {
        paid: paidOrders.filter(o => o.order_type === 'pre-order').reduce((sum, o) => sum + (o.total || 0), 0),
        pending: pendingOrders.filter(o => o.order_type === 'pre-order').reduce((sum, o) => sum + (o.total || 0), 0),
      };
      const regularRevenue = {
        paid: paidOrders.filter(o => o.order_type !== 'pre-order').reduce((sum, o) => sum + (o.total || 0), 0),
        pending: pendingOrders.filter(o => o.order_type !== 'pre-order').reduce((sum, o) => sum + (o.total || 0), 0),
      };

      // Daily revenue for chart (last 7 days)
      const dailyRevenue = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
        const dateStr = date.toISOString().split('T')[0];
        const dayOrders = paidOrders.filter(o => o.created_at?.startsWith(dateStr));
        const dayTotal = dayOrders.reduce((sum, o) => sum + (o.total || 0), 0);
        dailyRevenue.push({
          date: dateStr,
          label: date.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' }),
          revenue: dayTotal,
          orders: dayOrders.length
        });
      }

      // Top products
      const productCounts = {};
      filteredOrders.forEach(order => {
        (order.items || []).forEach(item => {
          const name = item.package_name || item.product_name || item.package?.name || item.product?.name || 'Unknown';
          if (!productCounts[name]) {
            productCounts[name] = { count: 0, revenue: 0 };
          }
          productCounts[name].count += item.quantity || 1;
          productCounts[name].revenue += (item.price || 0) * (item.quantity || 1);
        });
      });
      const topProducts = Object.entries(productCounts)
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

      setReportData({
        totalOrders: filteredOrders.length,
        paidOrders: paidOrders.length,
        pendingOrders: pendingOrders.length,
        totalRevenue,
        pendingRevenue,
        revenueByMethod,
        preorderCount,
        regularCount,
        preorderRevenue,
        regularRevenue,
        dailyRevenue,
        topProducts,
        avgOrderValue: paidOrders.length > 0 ? totalRevenue / paidOrders.length : 0
      });
    } catch (error) {
      toast.error('Failed to load report data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount || 0);
  };

  const handleExportCSV = () => {
    if (!reportData) return;
    
    const csvRows = [
      ['Bright Eyes Photography - Revenue Report'],
      [`Generated: ${new Date().toLocaleString()}`],
      [`Date Range: ${dateRange === 'all' ? 'All Time' : dateRange}`],
      [''],
      ['Metric', 'Value'],
      ['Total Orders', reportData.totalOrders],
      ['Paid Orders', reportData.paidOrders],
      ['Pending Orders', reportData.pendingOrders],
      ['Total Revenue', formatCurrency(reportData.totalRevenue)],
      ['Pending Revenue', formatCurrency(reportData.pendingRevenue)],
      ['Average Order Value', formatCurrency(reportData.avgOrderValue)],
      [''],
      ['Revenue by Payment Method'],
      ['Card/Stripe', formatCurrency(reportData.revenueByMethod.card)],
      ['Cash', formatCurrency(reportData.revenueByMethod.cash)],
      ['e-Transfer', formatCurrency(reportData.revenueByMethod.etransfer)],
      [''],
      ['Revenue by Order Type'],
      ['Pre-Orders', formatCurrency(reportData.preorderRevenue)],
      ['Gallery Orders', formatCurrency(reportData.regularRevenue)],
      [''],
      ['Top Products', 'Units Sold', 'Revenue'],
      ...reportData.topProducts.map(p => [p.name, p.count, formatCurrency(p.revenue)])
    ];
    
    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `revenue-report-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-800">Revenue Reports</h1>
          <p className="text-stone-600">Track your business performance and revenue</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Date Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">Last 7 Days</SelectItem>
              <SelectItem value="30days">Last 30 Days</SelectItem>
              <SelectItem value="90days">Last 90 Days</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-stone-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Total Revenue</p>
                <p className="text-2xl font-bold text-stone-800">{formatCurrency(reportData?.totalRevenue)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-2 flex items-center text-sm">
              <span className="text-stone-500">{reportData?.paidOrders} paid orders</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Pending Revenue</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(reportData?.pendingRevenue)}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-full">
                <TrendingUp className="h-6 w-6 text-amber-600" />
              </div>
            </div>
            <div className="mt-2 flex items-center text-sm">
              <span className="text-stone-500">{reportData?.pendingOrders} pending orders</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Total Orders</p>
                <p className="text-2xl font-bold text-stone-800">{reportData?.totalOrders}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2 text-sm">
              <Badge variant="outline" className="text-xs">{reportData?.preorderCount} pre-orders</Badge>
              <Badge variant="outline" className="text-xs">{reportData?.regularCount} gallery</Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="border-stone-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-stone-500">Avg Order Value</p>
                <p className="text-2xl font-bold text-stone-800">{formatCurrency(reportData?.avgOrderValue)}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <BarChart3 className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-2 text-sm text-stone-500">
              Per paid order
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Revenue */}
        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-amber-600" />
              Daily Revenue (Last 7 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {reportData?.dailyRevenue?.map((day, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-24 text-sm text-stone-600">{day.label}</div>
                  <div className="flex-1 bg-stone-100 rounded-full h-6 overflow-hidden">
                    <div 
                      className="bg-amber-500 h-full rounded-full transition-all duration-500"
                      style={{ 
                        width: `${Math.max(5, (day.revenue / Math.max(...reportData.dailyRevenue.map(d => d.revenue), 1)) * 100)}%` 
                      }}
                    />
                  </div>
                  <div className="w-24 text-right">
                    <span className="font-medium">{formatCurrency(day.revenue)}</span>
                    <span className="text-xs text-stone-500 ml-1">({day.orders})</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Revenue by Type */}
        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="h-5 w-5 text-amber-600" />
              Revenue Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* By Order Type */}
              <div>
                <p className="text-sm font-medium text-stone-600 mb-2">By Order Type</p>
                <div className="space-y-2">
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Pre-Orders</span>
                      <span className="font-bold text-amber-700">{formatCurrency((reportData?.preorderRevenue?.paid || 0) + (reportData?.preorderRevenue?.pending || 0))}</span>
                    </div>
                    <div className="flex gap-4 mt-1 text-xs">
                      <span className="text-green-600">Paid: {formatCurrency(reportData?.preorderRevenue?.paid || 0)}</span>
                      {(reportData?.preorderRevenue?.pending || 0) > 0 && (
                        <span className="text-amber-600">Pending: {formatCurrency(reportData?.preorderRevenue?.pending || 0)}</span>
                      )}
                    </div>
                  </div>
                  <div className="p-3 bg-stone-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Gallery Orders</span>
                      <span className="font-bold text-stone-700">{formatCurrency((reportData?.regularRevenue?.paid || 0) + (reportData?.regularRevenue?.pending || 0))}</span>
                    </div>
                    <div className="flex gap-4 mt-1 text-xs">
                      <span className="text-green-600">Paid: {formatCurrency(reportData?.regularRevenue?.paid || 0)}</span>
                      {(reportData?.regularRevenue?.pending || 0) > 0 && (
                        <span className="text-amber-600">Pending: {formatCurrency(reportData?.regularRevenue?.pending || 0)}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* By Payment Method */}
              <div>
                <p className="text-sm font-medium text-stone-600 mb-2">By Payment Method</p>
                <div className="space-y-2">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Card/Stripe</span>
                      <span className="font-bold text-blue-700">{formatCurrency((reportData?.revenueByMethod?.card?.paid || 0) + (reportData?.revenueByMethod?.card?.pending || 0))}</span>
                    </div>
                    <div className="flex gap-4 mt-1 text-xs">
                      <span className="text-green-600">Paid: {formatCurrency(reportData?.revenueByMethod?.card?.paid || 0)}</span>
                      {(reportData?.revenueByMethod?.card?.pending || 0) > 0 && (
                        <span className="text-amber-600">Pending: {formatCurrency(reportData?.revenueByMethod?.card?.pending || 0)}</span>
                      )}
                    </div>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Cash</span>
                      <span className="font-bold text-green-700">{formatCurrency((reportData?.revenueByMethod?.cash?.paid || 0) + (reportData?.revenueByMethod?.cash?.pending || 0))}</span>
                    </div>
                    <div className="flex gap-4 mt-1 text-xs">
                      <span className="text-green-600">Paid: {formatCurrency(reportData?.revenueByMethod?.cash?.paid || 0)}</span>
                      {(reportData?.revenueByMethod?.cash?.pending || 0) > 0 && (
                        <span className="text-amber-600">Pending: {formatCurrency(reportData?.revenueByMethod?.cash?.pending || 0)}</span>
                      )}
                    </div>
                  </div>
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">e-Transfer</span>
                      <span className="font-bold text-purple-700">{formatCurrency((reportData?.revenueByMethod?.etransfer?.paid || 0) + (reportData?.revenueByMethod?.etransfer?.pending || 0))}</span>
                    </div>
                    <div className="flex gap-4 mt-1 text-xs">
                      <span className="text-green-600">Paid: {formatCurrency(reportData?.revenueByMethod?.etransfer?.paid || 0)}</span>
                      {(reportData?.revenueByMethod?.etransfer?.pending || 0) > 0 && (
                        <span className="text-amber-600">Pending: {formatCurrency(reportData?.revenueByMethod?.etransfer?.pending || 0)}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Products */}
      <Card className="border-stone-200">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-amber-600" />
            Top Selling Products
          </CardTitle>
          <CardDescription>Best performing products by revenue</CardDescription>
        </CardHeader>
        <CardContent>
          {reportData?.topProducts?.length > 0 ? (
            <div className="space-y-3">
              {reportData.topProducts.map((product, idx) => (
                <div key={idx} className="flex items-center gap-4 p-3 bg-stone-50 rounded-lg">
                  <div className="flex items-center justify-center w-8 h-8 bg-amber-100 rounded-full text-amber-700 font-bold text-sm">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-stone-800">{product.name}</p>
                    <p className="text-sm text-stone-500">{product.count} units sold</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-amber-600">{formatCurrency(product.revenue)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-stone-500">
              No product data available for this period
            </div>
          )}
        </CardContent>
      </Card>

      {/* Profitability from Dashboard Stats */}
      {stats && (
        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Profitability Summary (All Time)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-stone-50 rounded-lg text-center">
                <p className="text-sm text-stone-500">Revenue</p>
                <p className="text-xl font-bold text-stone-800">{formatCurrency(stats.total_revenue)}</p>
              </div>
              <div className="p-4 bg-stone-50 rounded-lg text-center">
                <p className="text-sm text-stone-500">Cost of Goods</p>
                <p className="text-xl font-bold text-stone-600">{formatCurrency(stats.total_cost)}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <p className="text-sm text-green-600">Gross Profit</p>
                <p className="text-xl font-bold text-green-700">{formatCurrency(stats.gross_profit)}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <p className="text-sm text-green-600">Profit Margin</p>
                <p className="text-xl font-bold text-green-700">{stats.profit_margin?.toFixed(1)}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportsPage;
